import { useMarketplaceLabel, marketplaceExportFields } from "../configurazione/marketplaceDisplay";
import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLogisticaConfiguration } from "../configurazione/LogisticaConfigurationProvider";
import { orderedOptions, optionLabel } from "../configurazione/adapters";
import BlockSkeleton from "../../interfaccia/loading/BlockSkeleton";
import axios from "axios";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import DataTable from "../../interfaccia/DataTableBase";
import umanizzaDataSenzaOre from "../../interfaccia/funzioniComuni/umanizzaDataSenzaOre";

function padTo2Digits(num) {
  return num.toString().padStart(2, "0");
}

function formatDateInput(date = new Date()) {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join("-");
}

function normalizeDateForCsv(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

function formatDateForTable(value) {
  if (!value) return "";
  try {
    return umanizzaDataSenzaOre(value);
  } catch (err) {
    return normalizeDateForCsv(value);
  }
}

function formatEuro(value) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (Number.isNaN(number)) return "";
  return number.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function escapeCsvValue(value) {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (
    stringValue.includes(";") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function formatArticoliForCsv(articoliArr = []) {
  if (!Array.isArray(articoliArr) || articoliArr.length === 0) return "";
  return articoliArr
    .map((art, idx) => {
      const importo = formatEuro(art.importoUnitario);
      return [
        `${idx + 1})`,
        art.codiceVsArticolo || "N/D",
        art.descrizioneArticolo || "N/D",
        `qta ${art.quantita ?? 0}`,
        importo ? `${importo} EUR` : "",
      ]
        .filter(Boolean)
        .join(" | ");
    })
    .join("\r\n");
}

function downloadCsvFromRows(rows, fileName) {
  const delimiter = ";";
  const headers = Object.keys(rows[0] || {});
  const lines = [headers.join(delimiter)];

  rows.forEach((row) => {
    const line = headers
      .map((header) => escapeCsvValue(row[header]))
      .join(delimiter);
    lines.push(line);
  });

  const csv = `\uFEFF${lines.join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function renderArticoli(articoliArr = []) {
  if (!Array.isArray(articoliArr) || articoliArr.length === 0) return "-";
  return (
    <div className="pv1">
      {articoliArr.map((art, idx) => (
        <div key={`${art.codiceVsArticolo || "art"}-${idx}`} className="mb1">
          <span className="b">{art.codiceVsArticolo || "N/D"}</span>
          {" - "}
          {art.descrizioneArticolo || "N/D"}
          {" - "}
          qta {art.quantita ?? 0}
        </div>
      ))}
    </div>
  );
}

function renderNote(noteArr = []) {
  if (!Array.isArray(noteArr) || noteArr.length === 0) return "-";
  return (
    <div className="pv1">
      {noteArr.map((nota, idx) => (
        <div key={`${nota.dataNota || "nota"}-${idx}`} className="mb1">
          <span className="b">{formatDateForTable(nota.dataNota)}</span>
          {" - "}
          {nota.contenutoNota || ""}
        </div>
      ))}
    </div>
  );
}

function stessaSelezione(richiesta, risposta) {
  return risposta &&
    risposta.campoData === richiesta.campoData &&
    risposta.dataStart === richiesta.dataStart &&
    risposta.dataEnd === richiesta.dataEnd &&
    Array.isArray(risposta.magazzini) &&
    risposta.magazzini.length === richiesta.magazzini.length &&
    richiesta.magazzini.every(value => risposta.magazzini.includes(value));
}

function StatDatiGraneCorrieri() {
  const marketplaceLabel = useMarketplaceLabel();
  const { getAccessTokenSilently } = useAuth0();
  const { snapshot, tuttiIcampiRicerca } = useLogisticaConfiguration();
  const partenze = tuttiIcampiRicerca.spedPartenzaDAEQ.dati;
  const gruppiPartenza = snapshot.configuration.fields.spedPartenzaDAEQ.extraOptions || [];
  const magazziniOptions = orderedOptions(partenze)
    .filter(value => !gruppiPartenza.some(option => option.value === value));
  const today = new Date();
  const startLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
  const defaultFilters = {
    campoData: "Data_partenza",
    dataStart: formatDateInput(startLastMonth),
    dataEnd: formatDateInput(endLastMonth),
    magazzini: [],
  };

  const [input, setInput] = useState(defaultFilters);
  const [filtriApplicati, setFiltriApplicati] = useState(defaultFilters);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const rows = result?.data || [];
  const filtriVisualizzati = result?.filters || filtriApplicati;

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");
      setResult(null);
      try {
        const token = await getAccessTokenSilently();
        if (controller.signal.aborted) return;
        const params = new URLSearchParams({
          ...filtriApplicati,
          magazzini: JSON.stringify(filtriApplicati.magazzini),
        });
        const response = await axios.get(
          `${apiUrl}/contab/get/datiGraneCorrieri?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (!stessaSelezione(filtriApplicati, response?.data?.filters)) {
          throw new Error("La API non ha confermato i filtri richiesti. Ricarica dopo l'aggiornamento del servizio.");
        }
        setResult({ data: response.data.data || [], filters: filtriApplicati });
      } catch (err) {
        if (!controller.signal.aborted && !axios.isCancel(err)) {
          setError(err.response?.data?.error || err.message || "Errore caricamento dati grane corrieri");
          setResult(null);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [getAccessTokenSilently, filtriApplicati]);

  const columns = useMemo(
    () => [
      {
        selector: (row) => row.tipoProblema,
        name: "Tipo problema",
        sortable: true,
        grow: 1.2,
      },
      {
        selector: (row) => row.dataProblema,
        name: "Data problema",
        sortable: true,
        width: "140px",
        format: (row) => formatDateForTable(row.dataProblema),
      },
      {
        selector: (row) => row.numeroOrdinePrestashop,
        name: "N. ordine prestashop",
        sortable: true,
        width: "170px",
      },
      {
        selector: (row) => row.rifOrdineMarketplace,
        name: "Rif. ordine marketplace",
        sortable: true,
        grow: 1.2,
      },
      {
        selector: (row) => row.partenzaDa,
        name: "Partenza da",
        sortable: true,
        grow: 1.2,
        format: (row) => optionLabel(partenze, row.partenzaDa) || "",
      },
      {
        selector: (row) => row.corriere,
        name: "Corriere",
        sortable: true,
        width: "130px",
      },
      {
        selector: (row) => row.paeseSpedizione,
        name: "Paese spedizione",
        sortable: true,
        width: "140px",
      },
      {
        selector: (row) => row.tipoReso,
        name: "Tipo reso",
        sortable: false,
        grow: 1.1,
      },
      {
        selector: (row) => row.totaleRimborso,
        name: "Totale rimborso",
        sortable: true,
        width: "150px",
        format: (row) =>
          row.totaleRimborso || Number(row.totaleRimborso) === 0
            ? `${formatEuro(row.totaleRimborso)} €`
            : "",
      },
      {
        selector: (row) => row.articoliArr,
        name: "Articoli (array)",
        sortable: false,
        grow: 2,
        cell: (row) => renderArticoli(row.articoliArr),
      },
      {
        selector: (row) => row.noteArr,
        name: "Note (array)",
        sortable: false,
        grow: 2.3,
        cell: (row) => renderNote(row.noteArr),
      },
    ],
    [partenze],
  );

  function handleChange(evt) {
    const { name, value, selectedOptions } = evt.target;
    const nextValue = name === "magazzini"
      ? Array.from(selectedOptions, option => option.value)
      : value;
    setInput((prev) => ({ ...prev, [name]: nextValue }));
  }

  function applicaRange(evt) {
    evt.preventDefault();
    if (input.dataStart && input.dataEnd && input.dataStart > input.dataEnd) {
      setError("La data iniziale non può essere successiva alla data finale");
      return;
    }
    setError("");
    setResult(null);
    setLoading(true);
    setFiltriApplicati({ ...input, magazzini: input.magazzini.slice() });
  }

  function esportaCsv() {
    if (loading || error || !result || !rows.length) return;

    const csvRows = rows.map((row) => ({
      "Tipo problema": row.tipoProblema || "",
      "Data problema": normalizeDateForCsv(row.dataProblema),
      "N. ordine prestashop": row.numeroOrdinePrestashop || "",
      "Rif. ordine marketplace": row.rifOrdineMarketplace || "",
      ...marketplaceExportFields(row.marketplace, marketplaceLabel),
      "Data ordine": normalizeDateForCsv(row.dataOrdine),
      "Data partenza": normalizeDateForCsv(row.dataPartenza),
      "Partenza da": row.partenzaDa || "",
      Corriere: row.corriere || "",
      "Totale importo ordine": row.totaleImportoOrdine ?? "",
      "Totale importo contrassegno": row.totaleImportoContrassegno ?? "",
      "Tipo ordine": row.tipoOrdine || "",
      "ID Spedizione Domino": row.idSpedizioneDomino || "",
      "Paese spedizione": row.paeseSpedizione || "",
      NomeCognome: row.nomeCognome || "",
      "C/O": row.careOf || "",
      Indirizzo: row.indirizzo || "",
      CAP: row.cap || "",
      Città: row.citta || "",
      Provincia: row.provincia || "",
      Tipo_reso: row.tipoReso || "",
      Totale_rimborso: row.totaleRimborso ?? "",
      "N. articoli": Array.isArray(row.articoliArr)
        ? row.articoliArr.length
        : 0,
      Articoli: formatArticoliForCsv(row.articoliArr),
    }));

    const dateLabel = result.filters.campoData === "dataGranaCorriere" ? "data-grana" : "data-partenza";
    const fileName = `grane-corrieri-${dateLabel}-${result.filters.dataStart}_${result.filters.dataEnd}.csv`;
    downloadCsvFromRows(csvRows, fileName);
  }

  return (
    <div className="sfondoBiancoTema pa3">
      <form onSubmit={applicaRange} className="w-100 flex flex-wrap justify-center items-end tc mb3">
        <div className="pa2 mw5">
          <label className="mr1 b" htmlFor="grane-campoData">Intervallo su</label>
          <select id="grane-campoData" name="campoData" value={input.campoData}
            onChange={handleChange} className="input-reset inputOrizzontale">
            <option value="Data_partenza">Data partenza</option>
            <option value="dataGranaCorriere">Data grana corriere</option>
          </select>
        </div>
        <div className="pa2 mw5">
          <label className="mr1 b" htmlFor="grane-dataStart">Da data</label>
          <input id="grane-dataStart" type="date" name="dataStart"
            onChange={handleChange} value={input.dataStart} max={input.dataEnd || undefined}
            className="input-reset inputOrizzontale" />
        </div>
        <div className="pa2 mw5">
          <label className="mr1 b" htmlFor="grane-dataEnd">a data</label>
          <input id="grane-dataEnd" type="date" name="dataEnd"
            onChange={handleChange} value={input.dataEnd} min={input.dataStart || undefined}
            className="input-reset inputOrizzontale" />
        </div>
        <div className="pa2 w-100 w-40-l tl">
          <label className="db mb1 b" htmlFor="grane-magazzini">Magazzini di partenza</label>
          <select id="grane-magazzini" name="magazzini" multiple size={5}
            value={input.magazzini} onChange={handleChange}
            aria-describedby="grane-magazzini-help"
            className="w-100 input-reset inputOrizzontale"
            style={{ height: "auto" }}>
            {magazziniOptions.map(value => (
              <option key={value} value={value}>{optionLabel(partenze, value)}</option>
            ))}
          </select>
          <div id="grane-magazzini-help" className="f7 mt1">
            Nessuna selezione: tutti i magazzini. Usa Ctrl/Cmd per selezionarne più di uno.
          </div>
          <button type="button" className={stilePulsanti.buttonClassesWhite}
            onClick={() => setInput(prev => ({ ...prev, magazzini: [] }))}>
            Tutti i magazzini
          </button>
        </div>
        <button type="submit" aria-label="Applica filtri"
          className="dim tastoInputOrizzontaleNoRadNoHeight br2 ml3 mr3">
          {stilePulsanti.tastoCercaAspetto}
        </button>
        <button type="button" onClick={esportaCsv}
          className={stilePulsanti.buttonClassesWhite}
          disabled={loading || !!error || !result || rows.length === 0}>
          Esporta CSV con dati grane
        </button>
      </form>

      <div className="gray tc mb3">
        Range applicato ({filtriVisualizzati.campoData === "dataGranaCorriere" ? "Data grana corriere" : "Data partenza"}):{" "}
        {filtriVisualizzati.dataStart || "senza limite"} → {filtriVisualizzati.dataEnd || "senza limite"}
        {" · "}Magazzini: {filtriVisualizzati.magazzini.length
          ? filtriVisualizzati.magazzini.map(value => optionLabel(partenze, value)).join(", ")
          : "tutti"}
      </div>

      {error && <div role="alert" className="dark-red tc mb3">{error}</div>}

      <DataTable
        data={rows}
        columns={columns}
        progressPending={loading}
        progressComponent={<BlockSkeleton height={180} className="w-100" aria-label="Caricamento dati grane corrieri" />}
        noDataComponent={
          <div className="pa4 tc">Nessuna spedizione con grana nel range.</div>
        }
        pagination
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        paginationPerPage={10}
        paginationComponentOptions={{
          rowsPerPageText: "",
          rangeSeparatorText: "di",
          noRowsPerPage: false,
          selectAllRowsItem: false,
          selectAllRowsItemText: "Seleziona tutti",
        }}
      />
    </div>
  );
}

export default StatDatiGraneCorrieri;
